import React, { useEffect, useState } from "react";
import axios from "axios";
import Plot from "react-plotly.js";
import "../styles/PagesModern.css";
import { getIconFileName } from "../utils/iconUtils";
import { useSeriesCategories } from "../hooks/useSeriesCategories";
import { useTrimmedSeriesIcons } from "../hooks/useTrimmedSeriesIcons";
import CategoryIcon from "./CategoryIcon";
import { LoadingPanel, PageHeader, PanelHeader, SegmentedControl, ToggleGroup } from "./RefreshUI";
import { CHART, chartAxis, chartBase, chartLegend, chartWatermark, panelChartWidth } from "../utils/chartTheme";
import { CATEGORIES, CATEGORY_LABELS, LICENSE_COLORS, licenseLetter } from "../utils/racingLabels";

function SeriesPopularity() {
        const [data, setData] = useState(null);
        const [strengthRange, setStrengthRange] = useState([0, 99999]);
        const [startTimesRange, setStartTimesRange] = useState([0, 24]);
        const [page, setPage] = useState(0);
        const itemsPerPage = window.innerWidth > 800 ? 14 : 8;
        const [filter, setFilter] = useState([]);
        const [filteredData1, setFilteredData1] = useState([]);
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
        const [seasonTrackData, setSeasonTrackData] = useState([]);
        const [wetTrack, setWetTrack] = useState(false);
        const [weekNumber, setWeekNumber] = useState(0);
        const [licenseFilter, setLicenseFilter] = useState([]);
        const [weekNumberSelected, setWeekNumberSelected] = useState(null);
        const [availableWeeks, setAvailableWeeks] = useState([]);
        const seriesCategoryMap = useSeriesCategories();

        const trimSeasonName = (originalSeasonName) => {
                // Add your trimming logic here (not working)

                // copy the others from below but in this format
                return originalSeasonName; // Default return if no condition matches
        };

        useEffect(() => {
                async function fetchWeekNumber() {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/week-number");
                                const jsonData = await response.json();
                                const numericWeek = parseInt(jsonData, 10);
                                setWeekNumber(numericWeek);
                                setWeekNumberSelected(numericWeek);
                        } catch (error) {
                                console.error("Error fetching week number:", error);
                        }
                }

                fetchWeekNumber();
        }, []);

        useEffect(() => {
                async function fetchAvailableWeeks() {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/sof-results/historic-weeks");
                                const weeks = await response.json();
                                setAvailableWeeks(weeks);
                        } catch (error) {
                                console.error("Error fetching available weeks:", error);
                        }
                }

                fetchAvailableWeeks();
        }, []);

        useEffect(() => {
                const checkMobile = () => setIsMobile(window.innerWidth <= 768);
                window.addEventListener("resize", checkMobile);
                return () => window.removeEventListener("resize", checkMobile);
        }, []);

        useEffect(() => {
                if (weekNumberSelected === null || weekNumber === 0) return;

                setData(null);
                const fetchData = async () => {
                        try {
                                let endpoint;
                                if (weekNumberSelected === 0) {
                                        endpoint = "https://iracing6-backend.herokuapp.com/api/sof-results/full-season";
                                } else if (weekNumberSelected === weekNumber) {
                                        endpoint = "https://iracing6-backend.herokuapp.com/api/sof-results/sofresults";
                                } else {
                                        endpoint = `https://iracing6-backend.herokuapp.com/api/sof-results/historic/${weekNumberSelected}`;
                                }

                                const result = await axios(endpoint);

                                // convert each item's start_time to hour of day in UTC
                                result.data.forEach((item) => {
                                        const startTime = new Date(item.start_time);
                                        item.start_time = startTime.getUTCHours();
                                });
                                setData(result.data);
                        } catch (error) {
                                console.error("Error fetching data: ", error);
                        }
                };

                fetchData();
        }, [weekNumberSelected, weekNumber]);

        useEffect(() => {
                let newFilteredData = data ? [...data] : []; // Check if data is not null before copying

                // Apply category filter using API data
                if (filter.length > 0 && Object.keys(seriesCategoryMap).length > 0) {
                        newFilteredData = newFilteredData.filter((item) => {
                                const category = seriesCategoryMap[item.season_name];
                                return filter.includes(category);
                        });
                }

                // Apply wet track filter
                if (wetTrack) {
                        newFilteredData = newFilteredData.filter((item) => item.precip_time_pct > 0);
                }

                // Apply license filter
                if (licenseFilter.length > 0) {
                        newFilteredData = newFilteredData.filter((item) => licenseFilter.includes(item.license_class));
                }

                // The rest of your logic to handle season-track mapping and setting states
                const seasonTrackMap = new Map();
                newFilteredData.forEach((item) => {
                        const trimmedSeasonName = trimSeasonName(item.season_name);
                        if (!seasonTrackMap.has(trimmedSeasonName)) {
                                seasonTrackMap.set(trimmedSeasonName, item.track_name);
                        }
                });

                setFilteredData1(newFilteredData);

                const combinedData = Array.from(seasonTrackMap, ([season, track]) => ({
                        season, // Already trimmed above
                        track,
                }));
                setSeasonTrackData(combinedData);
        }, [data, filter, wetTrack, licenseFilter, seriesCategoryMap]);

        // Method to get the icon URL
        const getIconUrl = (seasonName) => {
                const iconName = getIconFileName(seasonName);
                // Assuming the icons are stored in the public folder under assets
                return `${process.env.PUBLIC_URL}/assets/series-icons/${iconName}.png`;
        };

        // Function to trim season names for display
        const displayTrimSeasonName = (seasonName) => {
                return seasonName
                        .replace(/\d{4}\s+Season\s+\d+/g, "") // Remove any year and season number (e.g., "2024 Season 4", "2025 Season 2")
                        .replace(/-/g, " ") // Replace hyphens with spaces
                        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
                        .replace(/Fixed Fixed/g, "Fixed") // Replace "Fixed Fixed" with "Fixed"
                        .trim(); // Trim leading and trailing spaces
        };

        const calculateAverageSessionPerSeason = () => {
                const seasonSessions = {};
                const sessionCounts = {};
                const sessionPopulation = {};
                const seasonLicenseClass = {};

                const ignoredSeasons = ["NASCAR iRacing Series - 2023 Season Open", "NASCAR iRacing Series - 2023 Season Fixed", "eNASCAR Road To Pro Qual Series Round 1 - 2023 Season"];

                const filteredData = filteredData1.filter((item) => {
                        const isTimeInRange = (startTime, rangeStart, rangeEnd) => {
                                if (rangeStart <= rangeEnd) {
                                        // Normal range, e.g., 1 to 3
                                        return startTime >= rangeStart && startTime <= rangeEnd;
                                } else {
                                        // Wrap-around range, e.g., 23 to 3
                                        return startTime >= rangeStart || startTime <= rangeEnd;
                                }
                        };

                        return (
                                item.event_strength_of_field >= strengthRange[0] &&
                                item.event_strength_of_field <= strengthRange[1] &&
                                !ignoredSeasons.includes(item.season_name) &&
                                isTimeInRange(item.start_time, startTimesRange[0], startTimesRange[1])
                        );
                });

                filteredData.forEach((item) => {
                        const { season_name, session_id, population, license_class } = item;

                        // Ignore item if season_name is undefined
                        if (!season_name) {
                                return;
                        }

                        if (season_name in seasonSessions) {
                                seasonSessions[season_name].push(session_id);
                        } else {
                                seasonSessions[season_name] = [session_id];
                        }

                        // Track license class for each season (take first one found)
                        if (!(season_name in seasonLicenseClass)) {
                                seasonLicenseClass[season_name] = license_class;
                        }

                        if (session_id in sessionPopulation) {
                                sessionPopulation[session_id] += population;
                        } else {
                                sessionPopulation[session_id] = population;
                        }
                });

                for (let season in seasonSessions) {
                        const sessionIds = seasonSessions[season];
                        sessionCounts[season] = {};

                        sessionIds.forEach((sessionId) => {
                                if (sessionId in sessionCounts[season]) {
                                        sessionCounts[season][sessionId]++;
                                } else {
                                        sessionCounts[season][sessionId] = 1;
                                }
                        });
                }

                const averageRepetitionPerSeason = {};
                const averagePopulationPerSeason = {};
                for (let season in sessionCounts) {
                        const totalCount = Object.values(sessionCounts[season]).reduce((a, b) => a + b, 0);
                        averageRepetitionPerSeason[season] = totalCount / Object.keys(sessionCounts[season]).length;

                        let totalPopulation = 0;
                        for (let sessionId in sessionCounts[season]) {
                                totalPopulation += sessionPopulation[sessionId];
                        }
                        averagePopulationPerSeason[season] = totalPopulation / Object.keys(sessionCounts[season]).length;
                }

                return { averageRepetitionPerSeason, averagePopulationPerSeason, seasonLicenseClass };
        };

        const updateRange = (e, index) => {
                const newRange = [...strengthRange];
                newRange[index] = e.target.value === "Max" ? 99999 : parseInt(e.target.value, 10);
                setStrengthRange(newRange);
        };

        const updateHourRange = (e, index) => {
                const newRange = [...startTimesRange];
                newRange[index] = e.target.value === "Max" ? 99999 : parseInt(e.target.value, 10);
                setStartTimesRange(newRange);
        };

        const handleBarClick = (data) => {
                // Extract the index of the clicked bar
                const clickedBarIndex = data.points[0].pointIndex;

                // Get the original season name from the seasonsData array using the clicked bar's index
                const originalSeasonName = seasonsData[clickedBarIndex + page * itemsPerPage].season;

                // Construct the URL using the original season name
                const url = `/charts/SeriesLapTimeChart/${encodeURIComponent(originalSeasonName)}`;

                // Redirect the user to the desired URL
                window.location.href = url;
        };

        const handleSeriesLinkTable = (season) => {
                // https://iracingdata.com/charts/SeriesLapTimeChart/${encodeURIComponent(originalSeasonName)}
                const url = `/charts/SeriesLapTimeChart/${encodeURIComponent(season)}`;

                // Redirect the user to the desired URL
                window.location.href = url;
        };

        const { averageRepetitionPerSeason, averagePopulationPerSeason, seasonLicenseClass } = calculateAverageSessionPerSeason();

        const seasonsData = Object.keys(averageRepetitionPerSeason).map((season) => ({
                season,
                averageRepetition: averageRepetitionPerSeason[season],
                averagePopulation: averagePopulationPerSeason[season],
                licenseClass: seasonLicenseClass[season],
        }));

        seasonsData.sort((a, b) => b.averagePopulation - a.averagePopulation);

        // trimming season names
        const pageData = {
                seasons: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.season),
                repetitions: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.averageRepetition),
                populations: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.averagePopulation),
                licenseClasses: seasonsData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((item) => item.licenseClass),
        };

        // Create a separate array for Plotly with trimmed season names
        const plotlySeasonNames = pageData.seasons.map(displayTrimSeasonName);

        const trimmedIcons = useTrimmedSeriesIcons(pageData.seasons);
        const periodLabel = weekNumberSelected === 0 ? "full season" : `week ${weekNumberSelected}`;
        const header = (
                <PageHeader title="Series popularity" lede={`Season 4, 2026, ${periodLabel ?? ""}. Average entrants and splits per race.`}>
                        <SegmentedControl
                                label="Period"
                                value={weekNumberSelected === 0 ? "season" : weekNumberSelected === weekNumber ? "week" : null}
                                onChange={(value) => setWeekNumberSelected(value === "season" ? 0 : weekNumber)}
                                options={[
                                        { value: "week", content: "Current week" },
                                        { value: "season", content: "Full season" },
                                ]}
                        />
                </PageHeader>
        );

        if (!data || weekNumberSelected === null)
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        {header}
                                        <LoadingPanel />
                                </main>
                        </div>
                );

        const isWide = window.innerWidth > 800;
        const rowCount = Math.max(1, pageData.seasons.length);
        const figWidth = panelChartWidth();
        const margin = isWide ? { l: 160, r: 24, t: 8, b: 40 } : { l: 60, r: 4, t: 8, b: 70 };
        const figHeight = isWide ? 700 : Math.max(420, window.innerHeight - 260);
        const iconPlotWidth = Math.max(1, figWidth - margin.l - margin.r);
        const plotHeight = Math.max(1, figHeight - margin.t - margin.b);
        // Equal visible area balances wide wordmarks and tall badges. Pixel caps
        // keep either shape compact and independent of the other logos on the page.
        const maxIconWidth = isWide ? 55 : 34;
        const maxIconHeight = Math.min(isWide ? 30 : 20, (plotHeight / rowCount) * 0.55);
        const targetIconArea = isWide ? 1125 : 480;
        const images = trimmedIcons.map((icon, index) => {
                const ratio = Math.max(0.01, icon.ratio);
                const height = Math.min(Math.sqrt(targetIconArea / ratio), maxIconHeight, maxIconWidth / ratio);
                return {
                        source: icon.source,
                        xref: "paper",
                        yref: "y",
                        x: -(isWide ? 36 : 26) / iconPlotWidth,
                        y: plotlySeasonNames[index],
                        xanchor: "center",
                        yanchor: "middle",
                        sizex: (height * ratio) / iconPlotWidth,
                        sizey: (height * rowCount) / plotHeight,
                        sizing: "contain",
                        layer: "above",
                };
        });

        // License letters to the left of each series icon (desktop).
        const licenseAnnotations = pageData.seasons.map((season, index) => {
                const licenseClass = pageData.licenseClasses[index];
                const color = LICENSE_COLORS[licenseClass] || "#8d8d96";
                return {
                        xref: "paper",
                        yref: "y",
                        x: -100 / iconPlotWidth,
                        y: plotlySeasonNames[index],
                        xanchor: "center",
                        yanchor: "middle",
                        text: `<b>${licenseLetter(licenseClass)}</b>`,
                        showarrow: false,
                        font: { size: 11, color },
                        bgcolor: "rgba(0,0,0,0)",
                        bordercolor: color,
                        borderwidth: 1.5,
                        borderpad: 3,
                        captureevents: false,
                };
        });

        // Colored bar beside each icon on mobile (replaces license letters)
        const mobileIconBorders = pageData.seasons.map((season, index) => ({
                xref: "paper",
                yref: "y",
                x: -54 / iconPlotWidth,
                y: plotlySeasonNames[index],
                xanchor: "center",
                yanchor: "middle",
                text: "<b>│</b>",
                showarrow: false,
                font: { size: 28, color: LICENSE_COLORS[pageData.licenseClasses[index]] || "#8d8d96" },
                bgcolor: "rgba(0,0,0,0)",
                borderwidth: 0,
        }));

        const trackFor = (season) => seasonTrackData.find((item) => item.season === season)?.track || "Unknown";
        const firstRow = page * itemsPerPage + 1;
        const lastRow = Math.min((page + 1) * itemsPerPage, seasonsData.length);
        const pager = (
                <div className="pg-pager">
                        <button type="button" className="pg-pager__btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
                                Previous
                        </button>
                        <span>
                                {seasonsData.length ? `${firstRow}–${lastRow} of ${seasonsData.length} series` : "No series match these filters"}
                        </span>
                        <button type="button" className="pg-pager__btn" disabled={(page + 1) * itemsPerPage >= seasonsData.length} onClick={() => setPage(page + 1)}>
                                Next
                        </button>
                </div>
        );
        const toggleIn = (setter) => (value) => setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

        return (
                <div className="pg">
                        <main className="hp-main">
                                {header}

                                <section className="hp-panel pg-filters" aria-label="Filters">
                                        <div className="pg-field">
                                                <span className="pg-field__label">Category</span>
                                                <ToggleGroup
                                                        label="Category"
                                                        selected={filter}
                                                        onToggle={toggleIn(setFilter)}
                                                        options={CATEGORIES.map((cat) => ({ value: cat, title: CATEGORY_LABELS[cat], content: <CategoryIcon category={cat} size={16} /> }))}
                                                />
                                        </div>
                                        <div className="pg-field">
                                                <span className="pg-field__label">License</span>
                                                <ToggleGroup
                                                        label="License"
                                                        selected={licenseFilter}
                                                        onToggle={toggleIn(setLicenseFilter)}
                                                        options={["A", "B", "C", "D", "Rookie"].map((lic) => ({
                                                                value: lic,
                                                                title: lic === "Rookie" ? "Rookie" : `Class ${lic}`,
                                                                content: (
                                                                        <span className="pg-lic" style={{ "--pg-lic": LICENSE_COLORS[lic] }}>
                                                                                {licenseLetter(lic)}
                                                                        </span>
                                                                ),
                                                        }))}
                                                />
                                        </div>
                                        <div className="pg-field">
                                                <label className="pg-field__label" htmlFor="sp-sof-min">
                                                        Strength of field
                                                </label>
                                                <div className="pg-field__row">
                                                        <input id="sp-sof-min" type="number" className="pg-input" value={strengthRange[0]} onChange={(e) => updateRange(e, 0)} aria-label="Minimum strength of field" />
                                                        to
                                                        <input type="number" className="pg-input" value={strengthRange[1]} max="999999" onChange={(e) => updateRange(e, 1)} aria-label="Maximum strength of field" />
                                                </div>
                                        </div>
                                        <div className="pg-field">
                                                <label className="pg-field__label" htmlFor="sp-hour-min">
                                                        Hour of day (UTC), now {new Date().getUTCHours()}:00
                                                </label>
                                                <div className="pg-field__row">
                                                        <input id="sp-hour-min" type="number" className="pg-input pg-input--short" value={startTimesRange[0]} onChange={(e) => updateHourRange(e, 0)} aria-label="From hour" />
                                                        to
                                                        <input type="number" className="pg-input pg-input--short" value={startTimesRange[1]} max="24" onChange={(e) => updateHourRange(e, 1)} aria-label="To hour" />
                                                </div>
                                        </div>
                                        <div className="pg-field">
                                                <label className="pg-field__label" htmlFor="sp-week">
                                                        Week
                                                </label>
                                                <select id="sp-week" className="pg-input" value={weekNumberSelected ?? ""} onChange={(e) => setWeekNumberSelected(parseInt(e.target.value))}>
                                                        <option value={0}>All</option>
                                                        {availableWeeks.map((week) => (
                                                                <option key={week} value={week}>
                                                                        {week}
                                                                </option>
                                                        ))}
                                                        {weekNumber && !availableWeeks.includes(weekNumber) && (
                                                                <option key={weekNumber} value={weekNumber}>
                                                                        {weekNumber}
                                                                </option>
                                                        )}
                                                </select>
                                        </div>
                                        <label className="pg-switch">
                                                <input type="checkbox" checked={wetTrack} onChange={() => setWetTrack(!wetTrack)} />
                                                Rain only
                                        </label>
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader title="Most popular series" />
                                        <p className="hp-panel__note">Sorted by average race entrants. Select a bar to see that series' lap times.</p>
                                        <div className="hp-chart">
                                                <Plot
                                                        onClick={(clicked) => handleBarClick(clicked)}
                                                        data={[
                                                                {
                                                                        type: "bar",
                                                                        x: pageData.repetitions,
                                                                        y: plotlySeasonNames,
                                                                        orientation: "h",
                                                                        name: isWide ? "Avg splits" : "Splits",
                                                                        marker: { color: CHART.yellow },
                                                                        hovertemplate: isWide ? "<i>Average # of splits</i>: %{x}<extra></extra>" : "<i>Splits</i>: %{x}<extra></extra>",
                                                                },
                                                                {
                                                                        type: "bar",
                                                                        x: pageData.populations,
                                                                        y: plotlySeasonNames,
                                                                        orientation: "h",
                                                                        name: isWide ? "Avg race entrants" : "Entrants",
                                                                        marker: { color: CHART.blue },
                                                                        text: pageData.seasons.map((season, index) =>
                                                                                isWide ? `<b>${plotlySeasonNames[index]}</b>  ${trackFor(season)}` : `<b>${plotlySeasonNames[index]}</b><br>${trackFor(season)}`
                                                                        ),
                                                                        // Labels sit inside long bars and spill outside short ones rather than shrinking.
                                                                        textposition: "auto",
                                                                        insidetextanchor: "start",
                                                                        insidetextfont: { color: "#ffffff", family: CHART.font },
                                                                        outsidetextfont: { color: CHART.text, family: CHART.font },
                                                                        cliponaxis: false,
                                                                        hovertemplate: isWide ? "<i>Average race entrants</i>: %{x}<extra></extra>" : "<i>Entrants</i>: %{x}<br><b>%{y}</b><extra></extra>",
                                                                },
                                                        ]}
                                                        layout={{
                                                                ...chartBase(),
                                                                margin,
                                                                images,
                                                                annotations: [
                                                                        ...(isWide ? (pageData.seasons.length >= 10 ? licenseAnnotations : []) : mobileIconBorders),
                                                                        // Bars shorten toward the bottom; on wide screens sit just above the bottom-right legend.
                                                                        chartWatermark("bottom-right", isWide ? { y: 0.02, yshift: window.innerWidth < 900 ? 32 : 50 } : {}),
                                                                ],
                                                                barmode: "stack",
                                                                bargap: 0.28,
                                                                xaxis: { ...chartAxis(), title: { text: isWide ? "Entrants" : "", font: { size: 12, color: CHART.muted } }, fixedrange: true },
                                                                yaxis: { ...chartAxis(), autorange: false, range: [rowCount - 0.5, -0.5], showticklabels: false, showgrid: false, fixedrange: true },
                                                                legend: isWide
                                                                        ? chartLegend({ x: 0.99, y: 0.02, xanchor: "right", yanchor: "bottom" })
                                                                        : { ...chartLegend({ x: 0, y: -0.08, xanchor: "left", yanchor: "top" }), bgcolor: "rgba(0,0,0,0)", borderwidth: 0 },
                                                                width: figWidth,
                                                                height: figHeight,
                                                                hovermode: "closest",
                                                        }}
                                                        config={{ displayModeBar: false, scrollZoom: false, doubleClick: false }}
                                                />
                                        </div>
                                        {pager}
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader title="Series details" />
                                        <div className="pg-table-wrap">
                                                <table className="pg-table">
                                                        <thead>
                                                                <tr>
                                                                        {!isMobile && <th>License</th>}
                                                                        <th>Series</th>
                                                                        <th>Track</th>
                                                                        <th className="pg-num">Avg splits</th>
                                                                        <th className="pg-num">Avg entrants</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {pageData.seasons.map((season, index) => {
                                                                        const licenseClass = pageData.licenseClasses[index];
                                                                        return (
                                                                                <tr key={season} className="pg-row-link" onClick={() => handleSeriesLinkTable(season)}>
                                                                                        {!isMobile && (
                                                                                                <td>
                                                                                                        <span className="pg-lic" style={{ "--pg-lic": LICENSE_COLORS[licenseClass] }}>
                                                                                                                {licenseLetter(licenseClass)}
                                                                                                        </span>
                                                                                                </td>
                                                                                        )}
                                                                                        <td>
                                                                                                <span className="pg-series">
                                                                                                        <img src={getIconUrl(season)} alt="" />
                                                                                                        <span className="pg-series__name">{displayTrimSeasonName(season)}</span>
                                                                                                </span>
                                                                                        </td>
                                                                                        <td className="pg-muted">{trackFor(season)}</td>
                                                                                        <td className="pg-num">{pageData.repetitions[index].toFixed(0)}</td>
                                                                                        <td className="pg-num">{pageData.populations[index].toFixed(0)}</td>
                                                                                </tr>
                                                                        );
                                                                })}
                                                        </tbody>
                                                </table>
                                        </div>
                                        {pager}
                                </section>

                                <p className="pg-fine">
                                        This chart doesn't account for series that race hourly versus every few hours, and it uses session strength of field rather than individual iRatings.
                                </p>
                        </main>
                </div>
        );
}

export default SeriesPopularity;
